/**
 * metaLeadGen/filters.ts
 *
 * Filter application, filter option extraction, and previous-period row selection.
 *
 * Important:
 * - ID-based filtering (campaignId/adsetId/adId) — names can repeat across accounts.
 * - Empty filter arrays are treated as "no filter" (show all).
 * - Returns empty array (not error) when no rows match — service handles zero state.
 */

import type { MetaLeadGenRow, MetaLeadGenFilters, FilterOptions } from "./types";
import { classifyCreativeType } from "./creativeType";

// ─────────────────────────────────────────────
// APPLY FILTERS
// ─────────────────────────────────────────────
export function applyFilters(rows: MetaLeadGenRow[], filters: MetaLeadGenFilters): MetaLeadGenRow[] {
  return rows.filter(row => {
    // Date range — YYYY-MM-DD string comparison is safe and correct
    if (filters.dateRange) {
      if (row.date < filters.dateRange.start || row.date > filters.dateRange.end) return false;
    }

    // ID-based entity filters
    if (filters.campaignIds?.length && !filters.campaignIds.includes(row.campaignId)) return false;
    if (filters.adsetIds?.length && !filters.adsetIds.includes(row.adsetId)) return false;
    if (filters.adIds?.length && !filters.adIds.includes(row.adId)) return false;

    // Dimension filters
    if (filters.ages?.length && !filters.ages.includes(row.age)) return false;
    if (filters.genders?.length && !filters.genders.includes(row.gender)) return false;
    if (filters.devices?.length && !filters.devices.includes(row.device)) return false;
    if (filters.platforms?.length && !filters.platforms.includes(row.platform)) return false;
    if (filters.countries?.length && !filters.countries.includes(row.country)) return false;

    // Creative type filter (derived from ad name at filter time)
    if (filters.creativeTypes?.length) {
      const creativeType = classifyCreativeType(row.adName);
      if (!filters.creativeTypes.includes(creativeType)) return false;
    }

    return true;
  });
}

// ─────────────────────────────────────────────
// FILTER OPTIONS — unique values from the data
// ─────────────────────────────────────────────
export function getFilterOptions(rows: MetaLeadGenRow[]): FilterOptions {
  const campaigns = new Map<string, string>();
  const adsets = new Map<string, string>();
  const ads = new Map<string, string>();
  const ages = new Set<string>();
  const genders = new Set<string>();
  const devices = new Set<string>();
  const platforms = new Set<string>();
  const creativeTypes = new Set<string>();
  const countries = new Set<string>();

  for (const row of rows) {
    campaigns.set(row.campaignId, row.campaignName);
    adsets.set(row.adsetId, row.adsetName);
    ads.set(row.adId, row.adName);
    ages.add(row.age);
    genders.add(row.gender);
    devices.add(row.device);
    platforms.add(row.platform);
    countries.add(row.country);
    creativeTypes.add(classifyCreativeType(row.adName));
  }

  return {
    campaigns: Array.from(campaigns.entries()).map(([id, name]) => ({ id, name })),
    adsets: Array.from(adsets.entries()).map(([id, name]) => ({ id, name })),
    ads: Array.from(ads.entries()).map(([id, name]) => ({ id, name })),
    ages: Array.from(ages).sort(),
    genders: Array.from(genders).sort(),
    devices: Array.from(devices).sort(),
    platforms: Array.from(platforms).sort(),
    creativeTypes: Array.from(creativeTypes).sort(),
    countries: Array.from(countries).sort(),
  };
}

// ─────────────────────────────────────────────
// PREVIOUS PERIOD — mirror of date range shifted back by same duration
// e.g. Jan 10–23 (14 days) → previous = Dec 27 – Jan 9 (14 days)
// ─────────────────────────────────────────────
export function getPreviousPeriodRows(
  rows: MetaLeadGenRow[],
  dateRange: { start: string; end: string }
): MetaLeadGenRow[] {
  const startMs = new Date(dateRange.start).getTime();
  const endMs = new Date(dateRange.end).getTime();
  const durationMs = endMs - startMs + 86400000; // inclusive (add one day)

  const prevEndMs = startMs - 86400000;
  const prevStartMs = prevEndMs - durationMs + 86400000;

  const prevStart = new Date(prevStartMs).toISOString().slice(0, 10);
  const prevEnd = new Date(prevEndMs).toISOString().slice(0, 10);

  return rows.filter(row => row.date >= prevStart && row.date <= prevEnd);
}
